/**
 * PlanPage
 * Main plan workflow page with bottom-up composer UI
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  Loader2,
  ArrowLeft,
  Globe,
  Sparkles,
  ExternalLink
} from 'lucide-react';

import { plansApi } from './api/plansApi';
import FeedStack from './components/FeedStack';
import BottomComposer from './components/BottomComposer';
import Step0GoalOfferCard from './steps/Step0GoalOfferCard';
import Step1PersonaCard from './steps/Step1PersonaCard';
import Step2SegmentsCard from './steps/Step2SegmentsCard';
import Step2QualifiersCard from './steps/Step2QualifiersCard';
import Step3CampaignsCard from './steps/Step3CampaignsCard';
import StepPlaceholderCard from './steps/StepPlaceholderCard';
import SignalsPanel from './steps/SignalsPanel';
import KeywordSetupPanel from './components/KeywordSetupPanel';
import InsightsPanel from './components/InsightsPanel';

export default function PlanPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [composerMode, setComposerMode] = useState(null);
  const [editingStep0, setEditingStep0] = useState(false);
  const [editingStep1, setEditingStep1] = useState(false);
  const [editingStep2, setEditingStep2] = useState(false);
  const [editingStep2Qualifiers, setEditingStep2Qualifiers] = useState(false);
  const [editingStep3, setEditingStep3] = useState(false);
  
  // State for confirmed bucket list plan (for signals)
  const [confirmedPlanId, setConfirmedPlanId] = useState(null);
  const [confirmedCampaignIds, setConfirmedCampaignIds] = useState([]);
  const [assetBundle, setAssetBundle] = useState(null);
  
  // State for two-panel insights flow
  const [confirmedSeeds, setConfirmedSeeds] = useState(null);
  const [isSeedsConfirmed, setIsSeedsConfirmed] = useState(false);
  
  // State for asset's business context (for insights)
  const [assetContext, setAssetContext] = useState(null);

  useEffect(() => {
    loadPlan();
  }, [planId]);
  
  // Fetch asset data when plan loads for insights
  useEffect(() => {
    const fetchAssetContext = async () => {
      if (!plan?.asset_id || assetContext) return;
      
      try {
        console.log('[PlanPage] Fetching asset context for:', plan.asset_id);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/assets/${plan.asset_id}`);
        const asset = response.data;
        
        // Parse website_context if it's a string
        let ctx = asset.website_context;
        if (typeof ctx === 'string') {
          try { ctx = JSON.parse(ctx); } catch (e) { ctx = {}; }
        }
        ctx = ctx || {};
        
        // Build business analysis from asset context
        const businessAnalysis = {
          website_analysis: {
            name: asset.name || '',
            url: asset.url || '',
            description: ctx.website_description || '',
            about: ctx.website_description || '',
            target_audience: ctx.target_audience_hints || [],
            primary_goals: ctx.primary_goals || [],
            ctas: ctx.ctas || []
          },
          products_services: ctx.products_services || [],
          business_goals: (ctx.primary_goals || []).map(g => typeof g === 'string' ? { goal: g } : g)
        };
        
        console.log('[PlanPage] Asset context loaded:', { 
          name: asset.name, 
          products: businessAnalysis.products_services?.length || 0,
          goals: businessAnalysis.business_goals?.length || 0
        });
        
        setAssetContext(businessAnalysis);
      } catch (error) {
        console.error('[PlanPage] Error fetching asset context:', error.message);
      }
    };
    
    fetchAssetContext();
  }, [plan?.asset_id, assetContext]);

  // Check for existing bucket when plan loads successfully
  useEffect(() => {
    const checkExistingBucket = async () => {
      // Only run if we have a plan loaded and no confirmedPlanId yet
      if (!plan || !planId || confirmedPlanId) return;
      
      try {
        console.log('[PlanPage] Checking for existing bucket for plan:', planId);
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bucket-workflow/buckets/by-plan/${planId}`);
        
        if (response.data && response.data.bucket_id) {
          console.log('[PlanPage] Found existing bucket:', response.data.bucket_id);
          setConfirmedPlanId(response.data.bucket_id);
        } else {
          console.log('[PlanPage] No bucket found for this plan');
        }
      } catch (error) {
        console.log('[PlanPage] Error checking for bucket:', error.message);
      }
    };
    
    checkExistingBucket();
  }, [plan, planId, confirmedPlanId]);

  const loadPlan = async () => {
    setLoading(true);
    try {
      const data = await plansApi.getById(planId);
      setPlan(data);
    } catch (error) {
      console.error('Failed to load plan:', error);
      toast.error('Failed to load plan');
      navigate('/potential-campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Get step data from plan_state
  const getStepData = (stepId) => {
    const steps = plan?.plan_state?.steps || [];
    const step = steps.find(s => s.id === stepId);
    return step?.data || {};
  };

  const getStepStatus = (stepId) => {
    const steps = plan?.plan_state?.steps || [];
    const step = steps.find(s => s.id === stepId);
    return step?.status || 'active';
  };

  const isStep0Completed = () => {
    return getStepStatus('step0_goal_offer') === 'completed' && !editingStep0;
  };

  const isStep1Completed = () => {
    return getStepStatus('step1_personas') === 'completed' && !editingStep1;
  };

  const isStep2Completed = () => {
    return getStepStatus('step2_segments') === 'completed' && !editingStep2;
  };

  const isStep2QualifiersCompleted = () => {
    return getStepStatus('step2_qualifiers') === 'completed' && !editingStep2Qualifiers;
  };

  const isStep3Completed = () => {
    return getStepStatus('step3_campaigns') === 'completed' && !editingStep3;
  };

  // Handle Step 0 save
  const handleSaveStep0 = async (stepData) => {
    setSaving(true);
    try {
      const updatedPlan = await plansApi.updateStep0(planId, stepData);
      setPlan(updatedPlan);
      setEditingStep0(false);
      
      // Clear Step 3 campaigns since the goal/offer changed
      try {
        await plansApi.clearStep3(planId);
        // Reload to get the cleared state
        const refreshedPlan = await plansApi.getById(planId);
        setPlan(refreshedPlan);
      } catch (clearError) {
        console.log('No step3 to clear or clear failed');
      }
      
      toast.success('Goal saved - campaigns will need to be regenerated');
    } catch (error) {
      console.error('Failed to save step 0:', error);
      toast.error('Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  // Handle Step 1 save
  const handleSaveStep1 = async (stepData) => {
    setSaving(true);
    try {
      const updatedPlan = await plansApi.saveStep1(
        planId,
        stepData.selected_persona_ids,
        stepData.plan_personas
      );
      setPlan(updatedPlan);
      setEditingStep1(false);
      
      // Clear Step 3 campaigns since the personas changed
      try {
        await plansApi.clearStep3(planId);
        // Reload to get the cleared state
        const refreshedPlan = await plansApi.getById(planId);
        setPlan(refreshedPlan);
      } catch (clearError) {
        console.log('No step3 to clear or clear failed');
      }
      
      toast.success('Personas saved - campaigns will need to be regenerated');
    } catch (error) {
      console.error('Failed to save step 1:', error);
      toast.error('Failed to save personas');
    } finally {
      setSaving(false);
    }
  };

  // Handle Step 2 save
  const handleSaveStep2 = async (stepData) => {
    setSaving(true);
    try {
      await plansApi.saveStep2(
        planId,
        stepData.generated_segments,
        stepData.selected_segment_ids
      );
      
      // Clear Step 3 campaigns since the segments changed
      try {
        await plansApi.clearStep3(planId);
      } catch (clearError) {
        console.log('No step3 to clear or clear failed');
      }
      
      // Reload plan to get updated state
      const updatedPlan = await plansApi.getById(planId);
      setPlan(updatedPlan);
      setEditingStep2(false);
      toast.success('Segments saved - campaigns will need to be regenerated');
    } catch (error) {
      console.error('Failed to save step 2:', error);
      toast.error('Failed to save segments');
    } finally {
      setSaving(false);
    }
  };

  // Handle Qualifiers save
  const handleSaveQualifiers = async (stepData) => {
    // Qualifiers are auto-saved via the component's API call
    // Just refresh plan state
    const updatedPlan = await plansApi.getById(planId);
    setPlan(updatedPlan);
    setEditingStep2Qualifiers(false);
  };

  // Handle Qualifiers skip
  const handleSkipQualifiers = async () => {
    // Qualifiers are auto-skipped via the component's API call
    // Just refresh plan state
    const updatedPlan = await plansApi.getById(planId);
    setPlan(updatedPlan);
    setEditingStep2Qualifiers(false);
  };

  // Handle Qualifiers edit
  const handleEditQualifiers = () => {
    setEditingStep2Qualifiers(true);
  };

  // Handle Step 3 save
  const handleSaveStep3 = async (stepData) => {
    setSaving(true);
    try {
      await plansApi.saveStep3(
        planId,
        stepData.generated_campaigns,
        stepData.selected_campaign_ids,
        stepData.user_controls
      );
      // Reload plan to get updated state
      const updatedPlan = await plansApi.getById(planId);
      setPlan(updatedPlan);
      setEditingStep3(false);
      toast.success('Campaigns saved successfully');
    } catch (error) {
      console.error('Failed to save step 3:', error);
      toast.error('Failed to save campaigns');
    } finally {
      setSaving(false);
    }
  };

  // Handle when campaigns are confirmed to bucket list
  const handleBucketConfirmed = (bucketData) => {
    // bucketData contains: { confirmedPlanId, confirmedCampaignIds, assetBundle }
    if (bucketData?.confirmedPlanId) {
      setConfirmedPlanId(bucketData.confirmedPlanId);
      setConfirmedCampaignIds(bucketData.confirmedCampaignIds || []);
      setAssetBundle(bucketData.assetBundle || null);
    }
  };

  // Handle when signals are saved
  const handleSignalsSaved = (signals) => {
    console.log('Signals saved:', signals.length);
    toast.success(`${signals.length} signals linked to bucket list`);
  };

  // Handle adding custom options
  const handleAddCustomOption = async (type, value) => {
    try {
      const updatedPlan = await plansApi.addCustomOption(planId, type, value);
      setPlan(updatedPlan);
    } catch (error) {
      console.error('Failed to add custom option:', error);
      toast.error('Failed to add custom option');
    }
  };

  // Handle edit steps
  const handleEditStep0 = () => {
    setEditingStep0(true);
  };

  const handleEditStep1 = () => {
    setEditingStep1(true);
  };

  const handleEditStep2 = () => {
    setEditingStep2(true);
  };

  const handleEditStep3 = () => {
    setEditingStep3(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Plan not found</p>
          <Button onClick={() => navigate('/potential-campaigns')}>
            Back to Potential Campaigns
          </Button>
        </div>
      </div>
    );
  }

  const step0Data = getStepData('step0_goal_offer');
  const step1Data = getStepData('step1_personas');
  const step2Data = getStepData('step2_segments');
  const step2QualifiersData = getStepData('step2_qualifiers');
  const step3Data = getStepData('step3_campaigns');
  const customOptions = plan?.plan_state?.custom_options || {};

  // Check if we should show two-column layout (Step 2 is completed)
  const showTwoColumnLayout = isStep2Completed();

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="plan-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/potential-campaigns')}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h1 className="font-semibold text-foreground">{plan.name}</h1>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Globe className="w-3.5 h-3.5" />
                    <span>{plan.asset_name || 'Unknown Asset'}</span>
                    {plan.asset_url && (
                      <a
                        href={plan.asset_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {showTwoColumnLayout ? (
        /* Two-Column Layout: Plan Summary + Saved Qualifiers (left) | Active Step + Campaigns + Signals (right) */
        <div className="flex-1 overflow-hidden">
          <div className="h-full flex flex-col lg:flex-row">
            {/* Left Column - Plan Summary + Saved Qualifiers */}
            <aside className="w-full lg:w-[40%] lg:min-w-[400px] border-b lg:border-b-0 lg:border-r border-border bg-slate-50/50 dark:bg-slate-900/30 overflow-y-auto">
              <div className="p-4 lg:p-6 space-y-4">
                {/* Plan Summary Section */}
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  Plan Summary
                </div>
                
                {/* Step 0 - Goal & Offer (Compact/Completed View) */}
                <Step0GoalOfferCard
                  stepData={step0Data}
                  customOptions={customOptions}
                  isCompleted={true}
                  onSave={handleSaveStep0}
                  onEdit={handleEditStep0}
                  onAddCustomOption={handleAddCustomOption}
                  saving={saving}
                />

                {/* Step 1 - Personas (Compact/Completed View) */}
                <Step1PersonaCard
                  planId={planId}
                  stepData={step1Data}
                  isCompleted={true}
                  onSave={handleSaveStep1}
                  onEdit={handleEditStep1}
                  saving={saving}
                />

                {/* Step 2 - Segments (Compact/Completed View) */}
                <Step2SegmentsCard
                  planId={planId}
                  stepData={step2Data}
                  isCompleted={true}
                  onSave={handleSaveStep2}
                  onEdit={handleEditStep2}
                  saving={saving}
                />

                {/* Saved Qualifiers - Shows in left column when completed */}
                {isStep2QualifiersCompleted() && (
                  <Step2QualifiersCard
                    planId={planId}
                    stepData={step2QualifiersData}
                    step1Data={step1Data}
                    step2Data={step2Data}
                    isCompleted={true}
                    onSave={handleSaveQualifiers}
                    onSkip={handleSkipQualifiers}
                    saving={saving}
                  />
                )}
              </div>
            </aside>

            {/* Right Column - Active Qualifiers + Campaign Playbook + Signals */}
            <main className="flex-1 overflow-y-auto">
              <div className="p-4 lg:p-6 xl:p-8 space-y-4">
                {/* Step 2.5 - Qualifiers (Optional) - Only show if not yet completed */}
                {!isStep2QualifiersCompleted() && (
                  <Step2QualifiersCard
                    planId={planId}
                    stepData={step2QualifiersData}
                    step1Data={step1Data}
                    step2Data={step2Data}
                    isCompleted={false}
                    onSave={handleSaveQualifiers}
                    onSkip={handleSkipQualifiers}
                    saving={saving}
                  />
                )}

                {/* Two-Panel Market Insights Flow */}
                {isStep2QualifiersCompleted() && (
                  <div className="space-y-4">
                    {/* Panel 1: Keyword Setup */}
                    <KeywordSetupPanel
                      businessAnalysis={assetContext || {
                        website_analysis: {},
                        products_services: [],
                        business_goals: []
                      }}
                      qualifiers={step2QualifiersData}
                      channelFocus="search"
                      websiteId={plan?.asset_id}
                      onSeedsConfirmed={(seeds) => {
                        setConfirmedSeeds(seeds);
                        setIsSeedsConfirmed(true);
                        toast.success(`${seeds.length} keywords confirmed - generating insights...`);
                      }}
                      isComplete={isSeedsConfirmed}
                      confirmedSeeds={confirmedSeeds}
                    />
                    
                    {/* Panel 2: Market Insights (locked until seeds confirmed) */}
                    <InsightsPanel
                      businessAnalysis={assetContext || {
                        website_analysis: {},
                        products_services: [],
                        business_goals: []
                      }}
                      qualifiers={step2QualifiersData}
                      channelFocus="search"
                      websiteId={plan?.asset_id}
                      isQualifiersComplete={isStep2QualifiersCompleted()}
                      confirmedSeeds={confirmedSeeds}
                      isSeedsConfirmed={isSeedsConfirmed}
                      onEditSeeds={() => {
                        setIsSeedsConfirmed(false);
                      }}
                    />
                  </div>
                )}

                {/* Step 4: Campaign Playbook Section */}
                <div className={!isStep2QualifiersCompleted() ? "border-t border-border pt-4" : ""}>
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-4">
                    <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                    Step 4: Campaign Playbook
                  </div>
                  
                  {/* Campaigns */}
                  <Step3CampaignsCard
                    planId={planId}
                    planName={plan?.name}
                    assetId={plan?.asset_id}
                    assetName={plan?.asset_name}
                    stepData={step3Data}
                    step0Data={step0Data}
                    step1Data={step1Data}
                    step2Data={step2Data}
                    isCompleted={isStep3Completed()}
                    onSave={handleSaveStep3}
                    onEdit={handleEditStep3}
                    onBucketConfirmed={handleBucketConfirmed}
                    saving={saving}
                  />
                </div>

                {/* Connect Real-Time Signals - Shows below Campaign Playbook after bucket confirmation */}
                {confirmedPlanId ? (
                  <div className="border-t border-border pt-4 mt-4">
                    <SignalsPanel
                      assetId={plan?.asset_id}
                      confirmedPlanId={confirmedPlanId}
                      confirmedCampaignIds={confirmedCampaignIds}
                      step0Data={step0Data}
                      step1Data={step1Data}
                      step2Data={step2Data}
                      assetBundle={assetBundle}
                      onSignalsSaved={handleSignalsSaved}
                    />
                  </div>
                ) : isStep3Completed() ? (
                  <div className="border-t border-border pt-4 mt-4">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-6 text-center">
                      <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-3">
                        <Sparkles className="w-5 h-5 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2 text-sm">
                        Connect Real-Time Signals
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Add campaigns to the Bucket List to unlock real-time signal tracking.
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Next Step Placeholder - Only show when Step 3 is completed */}
                {isStep3Completed() && (
                  <div className="mt-4">
                    <StepPlaceholderCard
                      stepNumber={5}
                      title="Next: Create Landing Pages"
                      description="Generate personalized landing pages for your campaigns"
                      isLocked={true}
                    />
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      ) : (
        /* Single Column Layout (Steps 0-2 not yet completed) */
        <FeedStack>
          {/* Step 0 - Goal & Offer */}
          <Step0GoalOfferCard
            stepData={step0Data}
            customOptions={customOptions}
            isCompleted={isStep0Completed()}
            onSave={handleSaveStep0}
            onEdit={handleEditStep0}
            onAddCustomOption={handleAddCustomOption}
            saving={saving}
          />

          {/* Step 1 - Personas (show when Step 0 is completed) */}
          {isStep0Completed() && (
            <Step1PersonaCard
              planId={planId}
              stepData={step1Data}
              isCompleted={isStep1Completed()}
              onSave={handleSaveStep1}
              onEdit={handleEditStep1}
              saving={saving}
            />
          )}

          {/* Step 2 - Segments (show when Step 1 is completed) */}
          {isStep1Completed() && (
            <Step2SegmentsCard
              planId={planId}
              stepData={step2Data}
              isCompleted={isStep2Completed()}
              onSave={handleSaveStep2}
              onEdit={handleEditStep2}
              saving={saving}
            />
          )}

          {/* Step 2.5 - Qualifiers (show when Step 2 is completed) */}
          {isStep2Completed() && (
            <Step2QualifiersCard
              planId={planId}
              stepData={step2QualifiersData}
              step1Data={step1Data}
              step2Data={step2Data}
              isCompleted={isStep2QualifiersCompleted()}
              onSave={handleSaveQualifiers}
              onSkip={handleSkipQualifiers}
              saving={saving}
            />
          )}
        </FeedStack>
      )}

      {/* Bottom Composer - Only show in single column mode */}
      {!showTwoColumnLayout && (
        <BottomComposer
          mode={composerMode}
          onSubmit={(value) => {
            setComposerMode(null);
          }}
          onCancel={() => setComposerMode(null)}
        />
      )}

      {/* Spacer for fixed bottom composer */}
      {!showTwoColumnLayout && <div className="h-20" />}
    </div>
  );
}
